// The project whose external experts are currently in externalExperts.
//
// Typed out rather than queried. An external expert carries no project of
// their own — one can be recommended for more than one — and the project's
// description and screener are not exposed through GraphQL today, so showing
// them would mean backend work for a panel that exists to give reviewers
// context. Update this when the pipeline is pointed at a different project.
export const PROJECT = {
    id: '6a10b08974472e1f917beef4',
    team: 'Illumina',
    title:
        'Understanding Laboratory Operational/Business Challenges and ' +
        'Opportunities for NGS Growth',
    description:
        'We are seeking input from pathologists and laboratory directors ' +
        'with firsthand experience managing the operational and business ' +
        'challenges of running a laboratory today. We are also interested ' +
        'in understanding the barriers and opportunities associated with ' +
        'expanding NGS testing.',
    targetN: 24,
    keywords: [
        'Pathology',
        'NGS',
        'Laboratory Operations',
        'Laboratory Director',
        'Laboratory',
        'Genomics',
        'Oncology',
        'Rare Disease Testing',
        'United States',
    ],
}

// What Stage 1 selected, read off the resulting pool rather than off a stored
// brief — the brief is generated at run time and not kept. So these are the
// specialties and credentials the 26,828 recommended people actually hold,
// which is the honest answer to "who did we search for".
export const SEARCHED_FOR = {
    recommendedCount: 26828,
    country: 'United States',
    // Share of a 4,000-person sample.
    specialties: [
        { name: 'Anatomic Pathology & Clinical Pathology', share: '77%' },
        { name: 'Anatomic Pathology', share: '13%' },
        { name: 'Dermatopathology', share: '9%' },
        { name: 'Cytopathology', share: '9%' },
        { name: 'Clinical Pathology / Laboratory Medicine', share: '7%' },
        { name: 'Hematology', share: '5%' },
        { name: 'Clinical Pathology', share: '2%' },
        { name: 'Molecular Genetic Pathology', share: '2%' },
        { name: 'Blood Banking & Transfusion Medicine', share: '1%' },
        { name: 'Neuropathology', share: '1%' },
    ],
    credentials: ['MD', 'DO', 'MD PhD'],
}

// The screener the project asks experts. Worth having beside the
// search results: it is the closest thing we have to a written statement of
// who would actually qualify, and it is what a reviewer is implicitly judging
// a page against.
export const SCREENER = [
    {
        question: 'Which of the following best describes your current role?',
        choices: [
            'Laboratory Director',
            'Pathologist (anatomic, molecular, clinical, or surgical)',
            'Laboratory Manager',
            'Clinical or Medical Geneticist',
            'Hospital Administrator / Executive',
            'Physician (non-pathology specialty)',
        ],
    },
    {
        question:
            'Which of the following best describes the institution where you perform the majority of your work?',
        choices: [
            'Academic Medical Center (AMC)',
            'Community Hospital',
            'Point-of-Care Clinic / Physician Office Lab',
            'Government / VA / Military Hospital',
            'Commercial Reference Lab — General / Broad Menu',
            'Commercial Reference Lab — Specialty',
            'Biopharma-Affiliated Lab',
            'Academic research institution (non-clinical)',
            'Government research organization',
        ],
    },
    {
        question:
            'Is your institution part of a larger health system, integrated delivery network (IDN), or multi-hospital organization?',
        choices: ['Yes', 'No', 'Unsure'],
    },
    {
        question:
            'How many hospitals or clinical sites does your laboratory regularly provide routine testing services to?',
        choices: ['1', '2-5', '6-10', '11-20', 'More than 20', 'Unsure'],
    },
    {
        question:
            'How familiar are you with next-generation sequencing (NGS) as a technology?',
        choices: [
            'Very familiar — deep knowledge of NGS technology and applications',
            'Familiar — a good working understanding',
            'Slightly familiar — heard of NGS but limited knowledge',
            'Not familiar',
            'Never heard of it',
        ],
    },
    {
        question:
            'Which of the following types of genetic testing does your lab currently perform, in-house or via send-out? (select all that apply)',
        choices: [
            'Chromosomal microarray (CMA / SNP array)',
            'Single-gene sequencing',
            'Disease-specific multi-gene panels (fewer than 50 genes)',
            'Broad or comprehensive multi-gene panels (50+ genes)',
            'Whole exome sequencing (WES)',
            'Whole genome sequencing (WGS)',
            'None, our lab does not perform any genetic testing',
            'Unsure',
        ],
    },
    {
        question: "How is your lab's NGS testing currently distributed?",
        choices: [
            'All performed in-house',
            'Hybrid (some in-house, some sent out)',
            'All sent out to a reference lab',
            'Unsure',
        ],
    },
    {
        question:
            'Which of the following best describes your primary role in the decision-making process for purchasing new NGS testing instruments?',
        choices: [
            'Final decision-maker',
            'Influencer',
            'Committee member or participant',
            'Proposal writer',
            'Evaluator / recommender',
            'No role in purchasing decisions',
            'Unsure',
        ],
    },
]

// Answer these wrong and the expert is out, so they are the sharpest
// statement of who the project wants.
export const KNOCKOUTS = [
    'Do you currently work in an academic medical center, community hospital, reference laboratory or point-of-care clinic/physician office laboratory?',
    'Is your primary workplace a clinical diagnostics laboratory performing patient-level diagnostic testing for patient care?',
    'Are you familiar with next-generation sequencing (NGS) technology?',
]
